import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "nexoriq_admin_token";

const CS_BLANK = {
  id: "",
  client: "",
  industry: "",
  category: "",
  title: "",
  summary: "",
  metrics: [],
  tags: [],
  img: "",
};

const PR_BLANK = {
  id: "",
  date: "",
  title: "",
  excerpt: "",
  body: "",
  tags: [],
};

function tokens(v) {
  return (v || "").split(",").map((s) => s.trim()).filter(Boolean);
}
function metricsParse(str) {
  // Format per line: "Label = value"
  return (str || "")
    .split("\n")
    .map((l) => l.split("=").map((s) => s.trim()))
    .filter(([a, b]) => a && b)
    .map(([label, value]) => ({ label, value }));
}
function metricsStr(arr) {
  return (arr || []).map((m) => `${m.label} = ${m.value}`).join("\n");
}

export default function ContentPanel() {
  const token = localStorage.getItem(TOKEN_KEY);
  const auth = { headers: { "X-Admin-Token": token } };

  const [cases, setCases] = useState([]);
  const [press, setPress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCase, setEditingCase] = useState(null);
  const [editingPress, setEditingPress] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/case-studies`),
        axios.get(`${API}/press-releases`),
      ]);
      setCases(c.data || []);
      setPress(p.data || []);
    } catch (err) {
      console.warn("content: load failed", err);
      toast.error("Failed to load content.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // --- Case studies ---
  const saveCase = async () => {
    setSaving(true);
    try {
      const isNew = !cases.find((c) => c.id === editingCase.id);
      const payload = {
        client: editingCase.client,
        industry: editingCase.industry,
        category: editingCase.category,
        title: editingCase.title,
        summary: editingCase.summary,
        metrics: editingCase.metrics || [],
        tags: editingCase.tags || [],
        img: editingCase.img || null,
      };
      if (isNew) {
        await axios.post(`${API}/admin/case-studies`, { id: editingCase.id || undefined, ...payload }, auth);
        toast.success("Case study created.");
      } else {
        await axios.put(`${API}/admin/case-studies/${editingCase.id}`, payload, auth);
        toast.success("Case study updated.");
      }
      setEditingCase(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCase = async (id) => {
    if (!window.confirm(`Delete case study "${id}"?`)) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, auth);
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  // --- Press releases ---
  const savePress = async () => {
    setSaving(true);
    try {
      const isNew = !press.find((p) => p.id === editingPress.id);
      const payload = {
        date: editingPress.date,
        title: editingPress.title,
        excerpt: editingPress.excerpt,
        body: editingPress.body || null,
        tags: editingPress.tags || [],
      };
      if (isNew) {
        await axios.post(`${API}/admin/press-releases`, { id: editingPress.id || undefined, ...payload }, auth);
        toast.success("Press release created.");
      } else {
        await axios.put(`${API}/admin/press-releases/${editingPress.id}`, payload, auth);
        toast.success("Press release updated.");
      }
      setEditingPress(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deletePress = async (id) => {
    if (!window.confirm(`Delete press release "${id}"?`)) return;
    try {
      await axios.delete(`${API}/admin/press-releases/${id}`, auth);
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-16" data-testid="content-panel">
      {loading && (
        <div className="font-mono text-sm uppercase tracking-[0.2em] text-nx-steel2">→ Loading…</div>
      )}

      {/* Case Studies */}
      <section data-testid="cs-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy">
            Case studies <span className="text-nx-steel2 font-mono text-xs">({cases.length})</span>
          </h2>
          <button
            onClick={() => setEditingCase({ ...CS_BLANK })}
            className="nx-btn !py-2 !px-3 !text-[0.62rem]"
            data-testid="cs-add"
          >
            <Plus size={12} /> New case study
          </button>
        </div>
        <div className="border border-nx-navy">
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-nx-navy text-nx-bg font-mono text-[0.62rem] uppercase tracking-[0.2em]">
            <div className="col-span-3">ID / Client</div>
            <div className="col-span-6">Title</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {cases.map((c) => (
            <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 border-t border-nx-navy/15 items-start" data-testid={`cs-row-${c.id}`}>
              <div className="md:col-span-3">
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-nx-steel2">{c.id}</div>
                <div className="text-sm text-nx-navy font-medium leading-tight">{c.client}</div>
              </div>
              <div className="md:col-span-6 text-sm text-nx-navy2 leading-snug">{c.title}</div>
              <div className="md:col-span-2 text-xs text-nx-navy2">{c.category}</div>
              <div className="md:col-span-1 flex md:justify-end gap-3">
                <button onClick={() => setEditingCase({ ...c })} title="Edit" className="text-nx-navy hover:text-nx-accent" data-testid={`cs-edit-${c.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => deleteCase(c.id)} title="Delete" className="text-nx-navy hover:text-nx-accent" data-testid={`cs-delete-${c.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {!cases.length && !loading && (
            <div className="p-6 text-nx-navy2 text-sm">No case studies yet.</div>
          )}
        </div>
      </section>

      {/* Press releases */}
      <section data-testid="pr-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-cabinet font-black text-2xl md:text-3xl tracking-tight text-nx-navy">
            Press releases <span className="text-nx-steel2 font-mono text-xs">({press.length})</span>
          </h2>
          <button
            onClick={() => setEditingPress({ ...PR_BLANK })}
            className="nx-btn !py-2 !px-3 !text-[0.62rem]"
            data-testid="pr-add"
          >
            <Plus size={12} /> New press release
          </button>
        </div>
        <div className="border border-nx-navy">
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-nx-navy text-nx-bg font-mono text-[0.62rem] uppercase tracking-[0.2em]">
            <div className="col-span-2">Date</div>
            <div className="col-span-9">Title</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {press.map((p) => (
            <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 border-t border-nx-navy/15 items-start" data-testid={`pr-row-${p.id}`}>
              <div className="md:col-span-2 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-nx-steel2">{p.date}</div>
              <div className="md:col-span-9">
                <div className="text-sm text-nx-navy font-medium leading-tight">{p.title}</div>
                <div className="text-xs text-nx-steel2 mt-0.5">{p.id}</div>
              </div>
              <div className="md:col-span-1 flex md:justify-end gap-3">
                <button onClick={() => setEditingPress({ ...p })} title="Edit" className="text-nx-navy hover:text-nx-accent" data-testid={`pr-edit-${p.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => deletePress(p.id)} title="Delete" className="text-nx-navy hover:text-nx-accent" data-testid={`pr-delete-${p.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {!press.length && !loading && (
            <div className="p-6 text-nx-navy2 text-sm">No press releases yet.</div>
          )}
        </div>
      </section>

      {/* Case study editor */}
      {editingCase && (
        <Editor onClose={() => setEditingCase(null)} title={cases.find((c) => c.id === editingCase.id) ? "Edit case study" : "New case study"} testid="cs-editor">
          <Grid>
            <Field label="Slug / ID" hint="lower-kebab. auto-generated on create if empty">
              <input value={editingCase.id} onChange={(e) => setEditingCase({ ...editingCase, id: e.target.value })} className={inp} data-testid="cs-f-id" />
            </Field>
            <Field label="Client"><input value={editingCase.client} onChange={(e) => setEditingCase({ ...editingCase, client: e.target.value })} className={inp} data-testid="cs-f-client" /></Field>
            <Field label="Industry"><input value={editingCase.industry} onChange={(e) => setEditingCase({ ...editingCase, industry: e.target.value })} className={inp} data-testid="cs-f-industry" /></Field>
            <Field label="Category"><input value={editingCase.category} onChange={(e) => setEditingCase({ ...editingCase, category: e.target.value })} className={inp} data-testid="cs-f-category" /></Field>
            <Field label="Title" full><input value={editingCase.title} onChange={(e) => setEditingCase({ ...editingCase, title: e.target.value })} className={inp} data-testid="cs-f-title" /></Field>
            <Field label="Summary" full><textarea rows={3} value={editingCase.summary} onChange={(e) => setEditingCase({ ...editingCase, summary: e.target.value })} className={inp + " resize-none"} data-testid="cs-f-summary" /></Field>
            <Field label="Tags (comma-separated)" full>
              <input value={(editingCase.tags || []).join(", ")} onChange={(e) => setEditingCase({ ...editingCase, tags: tokens(e.target.value) })} className={inp} data-testid="cs-f-tags" />
            </Field>
            <Field label="Metrics (one per line: Label = Value)" full>
              <textarea rows={3} value={metricsStr(editingCase.metrics)} onChange={(e) => setEditingCase({ ...editingCase, metrics: metricsParse(e.target.value) })} className={inp + " resize-none font-mono text-xs"} data-testid="cs-f-metrics" />
            </Field>
            <Field label="Image URL" full><input value={editingCase.img || ""} onChange={(e) => setEditingCase({ ...editingCase, img: e.target.value })} className={inp} data-testid="cs-f-img" /></Field>
          </Grid>
          <EditorFooter onCancel={() => setEditingCase(null)} onSave={saveCase} saving={saving} testid="cs-save" />
        </Editor>
      )}

      {/* Press release editor */}
      {editingPress && (
        <Editor onClose={() => setEditingPress(null)} title={press.find((p) => p.id === editingPress.id) ? "Edit press release" : "New press release"} testid="pr-editor">
          <Grid>
            <Field label="Slug / ID"><input value={editingPress.id} onChange={(e) => setEditingPress({ ...editingPress, id: e.target.value })} className={inp} data-testid="pr-f-id" /></Field>
            <Field label="Date label" hint='e.g. "Q4 · 2026"'><input value={editingPress.date} onChange={(e) => setEditingPress({ ...editingPress, date: e.target.value })} className={inp} data-testid="pr-f-date" /></Field>
            <Field label="Title" full><input value={editingPress.title} onChange={(e) => setEditingPress({ ...editingPress, title: e.target.value })} className={inp} data-testid="pr-f-title" /></Field>
            <Field label="Excerpt" full><textarea rows={3} value={editingPress.excerpt} onChange={(e) => setEditingPress({ ...editingPress, excerpt: e.target.value })} className={inp + " resize-none"} data-testid="pr-f-excerpt" /></Field>
            <Field label="Body (optional)" full><textarea rows={5} value={editingPress.body || ""} onChange={(e) => setEditingPress({ ...editingPress, body: e.target.value })} className={inp + " resize-none"} data-testid="pr-f-body" /></Field>
            <Field label="Tags (comma-separated)" full>
              <input value={(editingPress.tags || []).join(", ")} onChange={(e) => setEditingPress({ ...editingPress, tags: tokens(e.target.value) })} className={inp} data-testid="pr-f-tags" />
            </Field>
          </Grid>
          <EditorFooter onCancel={() => setEditingPress(null)} onSave={savePress} saving={saving} testid="pr-save" />
        </Editor>
      )}
    </div>
  );
}

const inp = "w-full bg-transparent border-b border-nx-navy/50 focus:border-nx-navy py-2 outline-none font-ibm text-nx-navy placeholder:text-nx-steel2 transition-colors";

function Field({ label, hint, full, children }) {
  return (
    <label className={"block " + (full ? "md:col-span-2" : "")}>
      <span className="flex items-center gap-2 mb-1 font-mono text-[0.6rem] uppercase tracking-[0.18em] text-nx-steel2">
        {label} {hint && <span className="normal-case tracking-normal text-[0.6rem] text-nx-steel2/80">— {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">{children}</div>;
}

function Editor({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-nx-navy/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-10" onClick={onClose} data-testid={`${testid}-overlay`}>
      <div className="bg-nx-bg border border-nx-navy max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="px-6 py-4 bg-nx-navy text-nx-bg flex items-center justify-between">
          <div className="font-cabinet font-black text-lg">{title}</div>
          <button onClick={onClose} className="text-nx-bg" aria-label="Close" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function EditorFooter({ onCancel, onSave, saving, testid }) {
  return (
    <div className="mt-6 pt-4 border-t border-nx-navy/20 flex justify-end gap-3">
      <button onClick={onCancel} className="nx-btn ghost !py-2 !px-3 !text-[0.62rem]">Cancel</button>
      <button onClick={onSave} disabled={saving} className="nx-btn !py-2 !px-3 !text-[0.62rem] disabled:opacity-60" data-testid={testid}>
        {saving ? <><Loader2 size={12} className="animate-spin" /> Saving</> : <><Save size={12} /> Save</>}
      </button>
    </div>
  );
}
